'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect } from 'react';
import { useMemo, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { applyLandingLanguage, useLandingLanguage } from '@/hooks/useLandingLanguage';
import { useWebsitePagesCopy } from '@/lib/cadrino-landing/useWebsitePagesCopy';
import { MetricWidget, MetricWidgetIcon, metricValueClassForVariant, type MetricIconVariant } from '@/components/ui/metric-widget';
import { CadrinoShell, Container, Section, SectionHeader, Card, MotionBlock, ContentCard, CardTitle, FeatureDetail } from './shared';
import { CadrinoMarketingHeader } from './CadrinoMarketingHeader';
import { CadrinoFooter } from './CadrinoFooter';
import { ProductOperationsShowcase } from './ProductOperationsShowcase';
import { ContactSection } from './ContactSection';
import { MarketingHero } from './MarketingHero';
import { PartnerKeyProvisionSimulation } from './simulation/PartnerKeyProvisionSimulation';
import { CheckCircle2, KeyRound, Layers, OctagonAlert, FileJson2, Clock3, LogOut, TimerReset, ShieldAlert, type LucideIcon } from 'lucide-react';
import type { SecurityPillar, SecurityPillarLabels, WebsitePageKey } from '@/lib/cadrino-landing/website-pages-copy';
function AnimatedMetricNumber(t0) {
  const $ = _c(8);
  const {
    value
  } = t0;
  const reducedMotion = useReducedMotion();
  let t1;
  if ($[0] !== value) {
    let t2;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = /[^0-9.-]/g;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    t1 = value.replace(t2, "");
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const numeric = Number(t1);
  const [display, setDisplay] = useState(reducedMotion || Number.isNaN(numeric) ? value : "0");
  let t2;
  let t3;
  if ($[3] !== numeric || $[4] !== reducedMotion || $[5] !== value) {
    t2 = () => {
      if (reducedMotion || Number.isNaN(numeric)) {
        setDisplay(value);
        return;
      }
      const start = performance.now();
      let frame = 0;
      const step = now => {
        const progress = Math.min((now - start) / 700, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        const nextValue = Math.round(numeric * eased);
        setDisplay(String(nextValue));
        if (progress < 1) {
          frame = requestAnimationFrame(step);
        }
      };
      frame = requestAnimationFrame(step);
      return () => cancelAnimationFrame(frame);
    };
    t3 = [numeric, reducedMotion, value];
    $[3] = numeric;
    $[4] = reducedMotion;
    $[5] = value;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  useEffect(t2, t3);
  return display;
}
function resolveQuickStatVisual(label: string, index: number): {
  icon: LucideIcon;
  variant: MetricIconVariant;
} {
  const normalized = label.toLowerCase();
  if (normalized.includes('kašn') || normalized.includes('late') || normalized.includes('delay')) {
    return {
      icon: Clock3,
      variant: 'warning'
    };
  }
  if (normalized.includes('rani') || normalized.includes('early') || normalized.includes('odlaz')) {
    return {
      icon: LogOut,
      variant: 'info'
    };
  }
  if (normalized.includes('ček') || normalized.includes('pending') || normalized.includes('wait')) {
    return {
      icon: TimerReset,
      variant: 'violet'
    };
  }
  if (normalized.includes('prav') || normalized.includes('rule') || normalized.includes('violation')) {
    return {
      icon: ShieldAlert,
      variant: 'danger'
    };
  }
  const fallback = [{
    icon: Clock3,
    variant: 'warning' as const
  }, {
    icon: LogOut,
    variant: 'info' as const
  }, {
    icon: TimerReset,
    variant: 'violet' as const
  }, {
    icon: ShieldAlert,
    variant: 'danger' as const
  }];
  return fallback[index % fallback.length];
}
function parseQaStep(question: string, index: number): {
  step: string;
  title: string;
} {
  const match = question.match(/^(\d+)\)\s*(.+)$/);
  if (!match) {
    return {
      step: String(index + 1).padStart(2, '0'),
      title: question
    };
  }
  return {
    step: String(Number(match[1])).padStart(2, '0'),
    title: match[2]
  };
}
function SecurityPillarsBlock(t0) {
  const $ = _c(7);
  const {
    pillars,
    labels
  } = t0;
  let t1;
  if ($[0] !== labels || $[1] !== pillars) {
    let t2;
    if ($[3] !== labels) {
      t2 = pillar => <ContentCard key={pillar.title}><CardTitle>{pillar.title}</CardTitle><dl className="mt-4 flex flex-1 flex-col gap-4"><FeatureDetail label={labels.problem} accent="problem">{pillar.problem}</FeatureDetail><FeatureDetail label={labels.solution} accent="behavior">{pillar.solution}</FeatureDetail><FeatureDetail label={labels.outcome} accent="outcome">{pillar.outcome}</FeatureDetail></dl></ContentCard>;
      $[3] = labels;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = pillars.map(t2);
    $[0] = labels;
    $[1] = pillars;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <div className="mt-12 grid gap-4 md:grid-cols-2">{t1}</div>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
function ComparisonBlock(t0) {
  const $ = _c(23);
  const {
    leftTitle,
    leftItems,
    rightTitle,
    rightItems
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <OctagonAlert className="h-4 w-4 text-rose-600" aria-hidden={true} />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== leftTitle) {
    t2 = <div className="flex items-center gap-2">{t1}<p className="text-xs font-semibold uppercase tracking-wider text-rose-700">{leftTitle}</p></div>;
    $[1] = leftTitle;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== leftItems) {
    t3 = leftItems.map(_temp);
    $[3] = leftItems;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = <ul className="mt-4 space-y-3 text-sm leading-relaxed text-slate-700">{t3}</ul>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t2 || $[8] !== t4) {
    t5 = <Card className="h-full border-rose-200/80 bg-rose-50/30">{t2}{t4}</Card>;
    $[7] = t2;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <CheckCircle2 className="h-4 w-4 text-cadrino-primary" aria-hidden={true} />;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== rightTitle) {
    t7 = <div className="flex items-center gap-2">{t6}<p className="text-xs font-semibold uppercase tracking-wider text-cadrino-primary">{rightTitle}</p></div>;
    $[11] = rightTitle;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== rightItems) {
    t8 = rightItems.map(_temp2);
    $[13] = rightItems;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t8) {
    t9 = <ul className="mt-4 space-y-3 text-sm leading-relaxed text-slate-800">{t8}</ul>;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t7 || $[18] !== t9) {
    t10 = <Card className="h-full border-cadrino-primary/25 bg-blue-50/30 ring-1 ring-cadrino-primary/10">{t7}{t9}</Card>;
    $[17] = t7;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10 || $[21] !== t5) {
    t11 = <div className="mt-8 grid gap-4 lg:grid-cols-2">{t5}{t10}</div>;
    $[20] = t10;
    $[21] = t5;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  return t11;
}
function _temp2(item_0) {
  return <li key={item_0} className="flex gap-2.5"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-cadrino-primary" aria-hidden={true} /><span>{item_0}</span></li>;
}
function _temp(item) {
  return <li key={item} className="flex gap-2.5"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-rose-500" aria-hidden={true} /><span>{item}</span></li>;
}
function OfficePresencePreview(t0) {
  const $ = _c(24);
  const {
    preview,
    className
  } = t0;
  const t1 = className ?? "mt-8 flex justify-center lg:justify-end";
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <p className="text-[10px] font-semibold uppercase tracking-wider text-cadrino-accent">Live</p>;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== preview.title) {
    t3 = <p className="text-xs font-semibold text-slate-900">{preview.title}</p>;
    $[1] = preview.title;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== preview.subtitle) {
    t4 = <p className="text-[11px] text-slate-500">{preview.subtitle}</p>;
    $[3] = preview.subtitle;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t3 || $[6] !== t4) {
    t5 = <div>{t2}{t3}{t4}</div>;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== preview.countLabel || $[9] !== preview.people.length) {
    t6 = <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">{preview.people.length} {preview.countLabel}</span>;
    $[8] = preview.countLabel;
    $[9] = preview.people.length;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t5 || $[12] !== t6) {
    t7 = <div className="mb-3 flex items-center justify-between">{t5}{t6}</div>;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== preview.people) {
    t8 = preview.people.map(_temp3);
    $[14] = preview.people;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t8) {
    t9 = <div className="space-y-1.5">{t8}</div>;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t7 || $[19] !== t9) {
    t10 = <div className="w-[230px] rounded-[2rem] border border-slate-200 bg-white p-2 shadow-lg shadow-slate-900/5"><div className="rounded-[1.65rem] border border-slate-100 bg-slate-50 p-3">{t7}{t9}</div></div>;
    $[18] = t7;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t1 || $[22] !== t10) {
    t11 = <MotionBlock className={t1}>{t10}</MotionBlock>;
    $[21] = t1;
    $[22] = t10;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  return t11;
}
function _temp3(name) {
  return <div key={name} className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-2.5 py-1.5"><span className="truncate text-xs font-medium text-slate-800">{name}</span><span className="ml-2 inline-block h-2 w-2 shrink-0 rounded-full bg-emerald-500" /></div>;
}
function PayrollReportPreview(t0) {
  const $ = _c(58);
  const {
    preview,
    bullets
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <CardTitle>Zašto ovo smanjuje rizik</CardTitle>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== bullets) {
    t2 = bullets.map(_temp4);
    $[1] = bullets;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <ContentCard>{t1}<ul className="mt-4 space-y-3 text-sm leading-relaxed text-cadrino-muted">{t2}</ul></ContentCard>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== preview.badge) {
    t4 = <p className="text-xs font-semibold uppercase tracking-wider text-cadrino-primary">{preview.badge}</p>;
    $[5] = preview.badge;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== preview.title) {
    t5 = <h3 className="mt-1 text-lg font-semibold text-slate-900">{preview.title}</h3>;
    $[7] = preview.title;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== preview.subtitle) {
    t6 = <p className="mt-1 text-sm text-cadrino-muted">{preview.subtitle}</p>;
    $[9] = preview.subtitle;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== preview.periodValue) {
    t7 = <span className="font-medium text-slate-700">{preview.periodValue}</span>;
    $[11] = preview.periodValue;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== preview.periodLabel || $[14] !== t7) {
    t8 = <p className="mt-3 text-xs text-slate-500">{preview.periodLabel}: {t7}</p>;
    $[13] = preview.periodLabel;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t4 || $[17] !== t5 || $[18] !== t6 || $[19] !== t8) {
    t9 = <div className="border-b border-slate-200 bg-slate-50/80 px-5 py-4">{t4}{t5}{t6}{t8}</div>;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== preview.colEmployee) {
    t10 = <th className="py-2 pr-3 font-medium">{preview.colEmployee}</th>;
    $[21] = preview.colEmployee;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== preview.colRegular) {
    t11 = <th className="py-2 pr-3 font-medium">{preview.colRegular}</th>;
    $[23] = preview.colRegular;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== preview.colOvertime) {
    t12 = <th className="py-2 pr-3 font-medium">{preview.colOvertime}</th>;
    $[25] = preview.colOvertime;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== preview.colNet) {
    t13 = <th className="py-2 font-medium">{preview.colNet}</th>;
    $[27] = preview.colNet;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t10 || $[30] !== t11 || $[31] !== t12 || $[32] !== t13) {
    t14 = <thead><tr className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">{t10}{t11}{t12}{t13}</tr></thead>;
    $[29] = t10;
    $[30] = t11;
    $[31] = t12;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== preview.rows) {
    t15 = preview.rows.map(_temp5);
    $[34] = preview.rows;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== t15) {
    t16 = <tbody>{t15}</tbody>;
    $[36] = t15;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t14 || $[39] !== t16) {
    t17 = <div className="overflow-x-auto px-5 py-4"><table className="w-full min-w-[420px] border-collapse text-left text-sm">{t14}{t16}</table></div>;
    $[38] = t14;
    $[39] = t16;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== preview.totalValue) {
    t18 = <span className="font-semibold text-slate-900">{preview.totalValue}</span>;
    $[41] = preview.totalValue;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== preview.totalLabel || $[44] !== t18) {
    t19 = <p className="text-sm text-slate-600">{preview.totalLabel}: {t18}</p>;
    $[43] = preview.totalLabel;
    $[44] = t18;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[46] !== preview.statusLabel) {
    t20 = <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">{preview.statusLabel}</span>;
    $[46] = preview.statusLabel;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] !== t19 || $[49] !== t20) {
    t21 = <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 bg-white px-5 py-3">{t19}{t20}</div>;
    $[48] = t19;
    $[49] = t20;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] !== t17 || $[52] !== t21 || $[53] !== t9) {
    t22 = <Card className="overflow-hidden border-cadrino-primary/20 p-0 ring-1 ring-cadrino-primary/10">{t9}{t17}{t21}</Card>;
    $[51] = t17;
    $[52] = t21;
    $[53] = t9;
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  let t23;
  if ($[55] !== t22 || $[56] !== t3) {
    t23 = <div className="mt-8 grid gap-4 lg:grid-cols-[0.9fr_1.1fr]">{t3}{t22}</div>;
    $[55] = t22;
    $[56] = t3;
    $[57] = t23;
  } else {
    t23 = $[57];
  }
  return t23;
}
function _temp5(row) {
  return <tr key={row.employee} className="border-b border-slate-100"><td className="py-2.5 pr-3 font-medium text-slate-800">{row.employee}</td><td className="py-2.5 pr-3 text-slate-600">{row.regularHours}</td><td className="py-2.5 pr-3 text-slate-600">{row.overtimeHours}</td><td className="py-2.5 font-semibold text-slate-800">{row.netHours}</td></tr>;
}
function _temp4(bullet) {
  return <li key={bullet} className="flex gap-2.5"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-cadrino-accent" aria-hidden={true} /><span>{bullet}</span></li>;
}
function ReportsWorkbenchPreview(t0) {
  const $ = _c(51);
  const {
    preview,
    cards,
    bullets
  } = t0;
  let t1;
  if ($[0] !== preview.badge) {
    t1 = <p className="text-xs font-semibold uppercase tracking-wider text-cadrino-primary">{preview.badge}</p>;
    $[0] = preview.badge;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== preview.title) {
    t2 = <h3 className="mt-1 text-lg font-semibold text-slate-900">{preview.title}</h3>;
    $[2] = preview.title;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== preview.subtitle) {
    t3 = <p className="mt-1 text-sm text-cadrino-muted">{preview.subtitle}</p>;
    $[4] = preview.subtitle;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = <div className="border-b border-slate-200 bg-gradient-to-br from-blue-50/70 via-white to-white px-5 py-4">{t1}{t2}{t3}</div>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== cards) {
    t5 = cards.map(_temp6);
    $[10] = cards;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t5) {
    t6 = <div className="space-y-3">{t5}</div>;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== preview.exportLabel) {
    t7 = <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{preview.exportLabel}</p>;
    $[14] = preview.exportLabel;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== preview.exportFormats) {
    t8 = preview.exportFormats.map(_temp7);
    $[16] = preview.exportFormats;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t8) {
    t9 = <div className="mt-2 flex flex-wrap gap-2">{t8}</div>;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t7 || $[21] !== t9) {
    t10 = <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">{t7}{t9}</div>;
    $[20] = t7;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== preview.metrics) {
    t11 = preview.metrics.map(_temp8);
    $[23] = preview.metrics;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t11) {
    t12 = <div className="grid grid-cols-2 gap-2">{t11}</div>;
    $[25] = t11;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== preview.readyStatus) {
    t13 = <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800">{preview.readyStatus}</div>;
    $[27] = preview.readyStatus;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t10 || $[30] !== t12 || $[31] !== t13) {
    t14 = <div className="space-y-3">{t10}{t12}{t13}</div>;
    $[29] = t10;
    $[30] = t12;
    $[31] = t13;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== t14 || $[34] !== t6) {
    t15 = <div className="grid gap-4 p-5 lg:grid-cols-[1.15fr_0.85fr]">{t6}{t14}</div>;
    $[33] = t14;
    $[34] = t6;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== t15 || $[37] !== t4) {
    t16 = <Card className="overflow-hidden border-cadrino-primary/20 p-0 ring-1 ring-cadrino-primary/10">{t4}{t15}</Card>;
    $[36] = t15;
    $[37] = t4;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== preview.valueTitle) {
    t17 = <CardTitle>{preview.valueTitle}</CardTitle>;
    $[39] = preview.valueTitle;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== bullets) {
    t18 = bullets.map(_temp9);
    $[41] = bullets;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== t18) {
    t19 = <ul className="mt-4 space-y-3 text-sm leading-relaxed text-cadrino-muted">{t18}</ul>;
    $[43] = t18;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== t17 || $[46] !== t19) {
    t20 = <ContentCard>{t17}{t19}</ContentCard>;
    $[45] = t17;
    $[46] = t19;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] !== t16 || $[49] !== t20) {
    t21 = <div className="mt-8 space-y-4">{t16}{t20}</div>;
    $[48] = t16;
    $[49] = t20;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  return t21;
}
function _temp9(bullet) {
  return <li key={bullet} className="flex gap-2.5"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-cadrino-accent" aria-hidden={true} /><span>{bullet}</span></li>;
}
function _temp8(m) {
  return <div key={m.label} className="rounded-xl border border-slate-200 bg-white px-3 py-2.5"><p className="text-[11px] uppercase tracking-wide text-slate-500">{m.label}</p><p className="mt-1 text-lg font-semibold text-slate-900">{m.value}</p></div>;
}
function _temp7(fmt) {
  return <span key={fmt} className="rounded-full border border-slate-300 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700">{fmt}</span>;
}
function _temp6(card) {
  return <div key={card.title} className="rounded-xl border border-slate-200 bg-white px-4 py-3"><p className="text-sm font-semibold text-slate-900">{card.title}</p><p className="mt-1 text-sm leading-relaxed text-cadrino-muted">{card.description}</p></div>;
}
function DeploymentStyleSection(t0) {
  const $ = _c(13);
  const {
    cards,
    bullets
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [KeyRound, Layers];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const icons = t1 as const;
  let t2;
  if ($[1] !== cards) {
    let t3;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = (card, i) => {
        const Icon = icons[i] ?? KeyRound;
        return <MotionBlock key={card.title} delay={i * 0.05}><ContentCard className="p-6 sm:p-7"><div className="flex items-start gap-3"><div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-cadrino-primary/15 text-cadrino-primary"><Icon className="h-5 w-5" aria-hidden={true} /></div><div><p className="text-xs font-medium uppercase tracking-wider text-cadrino-accent">{i === 0 ? "Model 1" : "Model 2"}</p><CardTitle className="mt-1">{card.title}</CardTitle></div></div><p className="mt-4 text-sm leading-relaxed text-cadrino-muted">{card.description}</p></ContentCard></MotionBlock>;
      };
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    t2 = cards.map(t3);
    $[1] = cards;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <div className="mt-12 grid gap-6 lg:grid-cols-2">{t2}</div>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== bullets) {
    t4 = bullets.map(_temp10);
    $[6] = bullets;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t4) {
    t5 = <MotionBlock className="mt-8"><ContentCard><ul className="space-y-3 text-sm leading-relaxed text-cadrino-muted">{t4}</ul></ContentCard></MotionBlock>;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t3 || $[11] !== t5) {
    t6 = <>{t3}{t5}</>;
    $[10] = t3;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
}
function _temp10(bullet) {
  return <li key={bullet} className="flex gap-2.5"><span className="mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full bg-cadrino-accent" aria-hidden={true} /><span>{bullet}</span></li>;
}
function TechnicalSnapshotBlock(t0) {
  const $ = _c(29);
  const {
    snapshot
  } = t0;
  const renderJsonLine = _temp11;
  let t1;
  if ($[0] !== snapshot.badge) {
    t1 = <p className="text-xs font-semibold uppercase tracking-wider text-cadrino-accent">{snapshot.badge}</p>;
    $[0] = snapshot.badge;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== snapshot.title) {
    t2 = <CardTitle className="mt-2">{snapshot.title}</CardTitle>;
    $[2] = snapshot.title;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== snapshot.points) {
    t3 = snapshot.points.map(_temp12);
    $[4] = snapshot.points;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <ul className="mt-4 space-y-3 text-sm leading-relaxed text-cadrino-muted">{t3}</ul>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t1 || $[9] !== t2 || $[10] !== t4) {
    t5 = <ContentCard>{t1}{t2}{t4}</ContentCard>;
    $[8] = t1;
    $[9] = t2;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <span className="flex h-6 w-6 items-center justify-center rounded-md bg-cadrino-primary/15 text-cadrino-primary"><FileJson2 className="h-3.5 w-3.5" aria-hidden={true} /></span>;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== snapshot.sampleTitle) {
    t7 = <div className="flex items-center gap-2">{t6}<p className="text-xs font-semibold tracking-wide text-cadrino-primary">{snapshot.sampleTitle}</p></div>;
    $[13] = snapshot.sampleTitle;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-600">JSON</span>;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t7) {
    t9 = <div className="flex items-center justify-between border-b border-slate-200 bg-gradient-to-r from-blue-50/80 via-white to-white px-4 py-3">{t7}{t8}</div>;
    $[16] = t7;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== snapshot.sampleLines) {
    let t11;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = (line_0, index_0) => renderJsonLine(line_0, index_0);
      $[20] = t11;
    } else {
      t11 = $[20];
    }
    t10 = snapshot.sampleLines.map(t11);
    $[18] = snapshot.sampleLines;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[21] !== t10) {
    t11 = <pre className="overflow-x-auto border-t border-slate-900/30 bg-gradient-to-b from-[#0b1733] to-[#0a142b] px-4 py-4 text-[13px] leading-7 text-slate-100"><code>{t10}</code></pre>;
    $[21] = t10;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t11 || $[24] !== t9) {
    t12 = <Card className="overflow-hidden border-cadrino-primary/25 p-0 ring-1 ring-cadrino-primary/10">{t9}{t11}</Card>;
    $[23] = t11;
    $[24] = t9;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t12 || $[27] !== t5) {
    t13 = <div className="mt-8 grid gap-4 lg:grid-cols-[1.05fr_0.95fr]">{t5}{t12}</div>;
    $[26] = t12;
    $[27] = t5;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  return t13;
}
function _temp12(point) {
  return <li key={point} className="flex gap-2.5"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-cadrino-primary" aria-hidden={true} /><span>{point}</span></li>;
}
function _temp11(line, index) {
  const trimmed = line.trim();
  if (trimmed === "{" || trimmed === "}") {
    return <span key={`${line}-${index}`} className="block text-slate-400">{line}</span>;
  }
  const keyValueMatch = line.match(/^(\s*)"([^"]+)"(\s*:\s*)(.+)$/);
  if (!keyValueMatch) {
    return <span key={`${line}-${index}`} className="block text-slate-200">{line}</span>;
  }
  const [, indent, key, separator, rawValue] = keyValueMatch;
  const valueWithoutComma = rawValue.endsWith(",") ? rawValue.slice(0, -1) : rawValue;
  const trailingComma = rawValue.endsWith(",") ? "," : "";
  let valueClassName = "text-amber-200";
  if (valueWithoutComma === "\"IN\"" || valueWithoutComma === "\"OUT\"") {
    valueClassName = "text-emerald-300";
  } else {
    if (/^"\d{4}-\d{2}-\d{2}T/.test(valueWithoutComma)) {
      valueClassName = "text-sky-300";
    } else {
      if (/^"EMP-/.test(valueWithoutComma) || /^"dev-/.test(valueWithoutComma)) {
        valueClassName = "text-violet-300";
      }
    }
  }
  return <span key={`${line}-${index}`} className="block text-slate-200"><span>{indent}</span><span className="text-cyan-300">"{key}"</span><span className="text-slate-400">{separator}</span><span className={valueClassName}>{valueWithoutComma}</span><span className="text-slate-400">{trailingComma}</span></span>;
}
export function CadrinoWebsitePage(t0) {
  const $ = _c(36);
  const {
    pageKey
  } = t0;
  const pages = useWebsitePagesCopy();
  const {
    language
  } = useLandingLanguage();
  const stepLabel = language === "en" ? "Step" : "Korak";
  const page = pages.pages[pageKey];
  let t1;
  let t2;
  if ($[0] !== language) {
    t1 = () => {
      applyLandingLanguage(language);
    };
    t2 = [language];
    $[0] = language;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <CadrinoMarketingHeader />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== page.hero.eyebrow || $[5] !== page.hero.highlights || $[6] !== page.hero.primaryCta || $[7] !== page.hero.secondaryCta || $[8] !== page.hero.secondaryHref || $[9] !== page.hero.subtitle || $[10] !== page.hero.title) {
    t4 = <MarketingHero variant="inner" eyebrow={page.hero.eyebrow} title={page.hero.title} subtitle={page.hero.subtitle} highlights={page.hero.highlights} primaryCtaLabel={page.hero.primaryCta} primaryCtaHref="#contact" secondaryCtaLabel={page.hero.secondaryCta} secondaryCtaHref={page.hero.secondaryHref} />;
    $[4] = page.hero.eyebrow;
    $[5] = page.hero.highlights;
    $[6] = page.hero.primaryCta;
    $[7] = page.hero.secondaryCta;
    $[8] = page.hero.secondaryHref;
    $[9] = page.hero.subtitle;
    $[10] = page.hero.title;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== pageKey) {
    t5 = pageKey === "product" && <ProductOperationsShowcase />;
    $[12] = pageKey;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== page.sections || $[15] !== stepLabel) {
    let t7;
    if ($[17] !== stepLabel) {
      t7 = (section, index) => <Section key={section.title} id={section.anchorId} className={index % 2 === 1 ? "border-t border-white/[0.08] bg-cadrino-surface/35" : "border-t border-white/[0.08]"}><Container><MotionBlock><SectionHeader title={section.title} subtitle={section.body} /></MotionBlock>{section.quickStats && section.quickStats.length > 0 && <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{section.quickStats.map(_temp13)}</div>}{section.reportsWorkbenchPreview && section.cards && section.bullets && <MotionBlock><ReportsWorkbenchPreview preview={section.reportsWorkbenchPreview} cards={section.cards} bullets={section.bullets} /></MotionBlock>}{section.technicalSnapshot && <MotionBlock><TechnicalSnapshotBlock snapshot={section.technicalSnapshot} /></MotionBlock>}{section.useLandingDeploymentStyle && section.cards && section.bullets && <DeploymentStyleSection cards={section.cards} bullets={section.bullets} />}{!section.useLandingDeploymentStyle && !section.reportsWorkbenchPreview && section.cards && section.cards.length > 0 && <div className={`mt-12 grid gap-4 ${section.cardsColumns === 2 ? "md:grid-cols-2" : "md:grid-cols-3"}`}>{section.cards.map(_temp14)}</div>}{section.payrollReportPreview && section.bullets && section.bullets.length > 0 && <MotionBlock><PayrollReportPreview preview={section.payrollReportPreview} bullets={section.bullets} /></MotionBlock>}{!section.useLandingDeploymentStyle && !section.reportsWorkbenchPreview && !section.payrollReportPreview && section.bullets && section.bullets.length > 0 && <MotionBlock className="mt-8"><Card className="border-cadrino-primary/15 bg-gradient-to-b from-white/[0.05] to-white/[0.02] p-5 sm:p-6"><ul className="grid gap-3 sm:grid-cols-2">{section.bullets.map(_temp15)}</ul></Card></MotionBlock>}{section.pillars && section.pillars.length > 0 && section.pillarLabels && <MotionBlock><SecurityPillarsBlock pillars={section.pillars} labels={section.pillarLabels} /></MotionBlock>}{section.compare && <MotionBlock><ComparisonBlock leftTitle={section.compare.leftTitle} leftItems={section.compare.leftItems} rightTitle={section.compare.rightTitle} rightItems={section.compare.rightItems} /></MotionBlock>}{section.standardsTable && <div className="mt-8 grid gap-6 lg:grid-cols-[minmax(0,1fr)_240px] lg:items-end"><MotionBlock className="overflow-x-auto"><div className="overflow-hidden rounded-2xl border border-white/[0.1] bg-cadrino-surface/80"><table className="w-full min-w-[680px] border-collapse text-left text-sm"><thead><tr className="border-b border-white/[0.12] bg-white/[0.03]">{section.standardsTable.headers.map(_temp16)}</tr></thead><tbody>{section.standardsTable.rows.map(_temp17)}</tbody></table></div></MotionBlock>{section.officePresencePreview && <OfficePresencePreview preview={section.officePresencePreview} className="flex justify-center lg:justify-end lg:self-end" />}</div>}{section.officePresencePreview && !section.standardsTable && <OfficePresencePreview preview={section.officePresencePreview} />}{section.qa && section.qa.length > 0 && <div className={`mt-8 grid gap-4 ${section.qa.length >= 4 ? "md:grid-cols-2" : "md:grid-cols-3"}`}>{section.qa.map((item, itemIndex) => {
              const parsed = parseQaStep(item.question, itemIndex);
              return <MotionBlock key={item.question} delay={itemIndex * 0.04}><Card className="h-full border-white/[0.12] bg-gradient-to-b from-white/[0.06] to-white/[0.02] p-5 transition-shadow hover:shadow-md"><div className="flex items-start justify-between gap-3"><span className="inline-flex items-center rounded-full border border-cadrino-primary/35 bg-cadrino-primary/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-cadrino-primary">{stepLabel} {parsed.step}</span><CheckCircle2 className="h-4 w-4 shrink-0 text-cadrino-accent" aria-hidden={true} /></div><h3 className="mt-3 text-base font-semibold text-slate-100">{parsed.title}</h3><p className="mt-2 text-sm leading-relaxed text-cadrino-muted">{item.answer}</p></Card></MotionBlock>;
            })}</div>}</Container></Section>;
      $[17] = stepLabel;
      $[18] = t7;
    } else {
      t7 = $[18];
    }
    t6 = page.sections.map(t7);
    $[14] = page.sections;
    $[15] = stepLabel;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[19] !== pageKey) {
    t7 = pageKey === "partners" && <Section className="border-t border-white/[0.08] bg-cadrino-surface/20 py-12 md:py-16"><Container><PartnerKeyProvisionSimulation /></Container></Section>;
    $[19] = pageKey;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== page.finalCta.body || $[22] !== page.finalCta.primaryCta || $[23] !== page.finalCta.qualifierChips || $[24] !== page.finalCta.title || $[25] !== pages.nav.product) {
    t8 = <ContactSection title={page.finalCta.title} subtitle={page.finalCta.body} primaryLabel={page.finalCta.primaryCta} primaryHref="#contact-form" secondaryLabel={pages.nav.product} secondaryHref="/product" qualifierChips={page.finalCta.qualifierChips} />;
    $[21] = page.finalCta.body;
    $[22] = page.finalCta.primaryCta;
    $[23] = page.finalCta.qualifierChips;
    $[24] = page.finalCta.title;
    $[25] = pages.nav.product;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== t4 || $[28] !== t5 || $[29] !== t6 || $[30] !== t7 || $[31] !== t8) {
    t9 = <main>{t4}{t5}{t6}{t7}{t8}</main>;
    $[27] = t4;
    $[28] = t5;
    $[29] = t6;
    $[30] = t7;
    $[31] = t8;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  let t10;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <CadrinoFooter />;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  let t11;
  if ($[34] !== t9) {
    t11 = <CadrinoShell>{t3}{t9}{t10}</CadrinoShell>;
    $[34] = t9;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  return t11;
}
function _temp17(row) {
  return <tr key={row.criterion} className="border-b border-white/[0.06] text-cadrino-muted transition-colors hover:bg-white/[0.03]"><td className="px-4 py-3.5 font-semibold text-slate-100">{row.criterion}</td><td className="px-4 py-3.5 text-slate-300">{row.pain}</td><td className="px-4 py-3.5 text-slate-100">{row.help}</td></tr>;
}
function _temp16(header, headerIndex) {
  return <th key={header} className={`px-4 py-3.5 text-xs font-semibold uppercase tracking-wide ${headerIndex === 0 ? "text-slate-200" : headerIndex === 1 ? "text-slate-300" : "text-cadrino-accent"}`}>{header}</th>;
}
function _temp15(bullet) {
  return <li key={bullet} className="rounded-lg border border-white/[0.08] bg-white/[0.03] px-3 py-2.5 text-sm leading-relaxed text-slate-200 transition-colors hover:bg-white/[0.06]"><span className="flex items-start gap-2.5"><span className="mt-[0.45rem] inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-cadrino-accent" aria-hidden={true} /><span>{bullet}</span></span></li>;
}
function _temp14(card, cardIndex) {
  return <MotionBlock key={card.title} delay={cardIndex * 0.04}><Card className="h-full"><h3 className="text-lg font-semibold text-slate-100">{card.title}</h3><p className="mt-3 text-sm leading-relaxed text-cadrino-muted">{card.description}</p></Card></MotionBlock>;
}
function _temp13(stat, statIndex) {
  const visual = resolveQuickStatVisual(stat.label, statIndex);
  return <MotionBlock key={stat.label} delay={statIndex * 0.04}><motion.div whileHover={{
      y: -3
    }} transition={{
      duration: 0.2,
      ease: "easeOut"
    }} className="h-full"><MetricWidget className="h-full border-border/80 bg-card/95 p-4 shadow-sm transition-shadow hover:shadow-md" title={stat.label} trailing={<MetricWidgetIcon icon={visual.icon} variant={visual.variant} />} value={<AnimatedMetricNumber value={String(stat.value)} />} valueClassName={metricValueClassForVariant(visual.variant)} subtitle={stat.hint} /></motion.div></MotionBlock>;
}